// `<ValidationSummary />` — top-of-form aggregation of every Zod
// safeParse error currently surfaced by `<Form>`. Each entry links to
// the field's input id (mirrors `FieldRenderer`'s id pattern:
// `${idPrefix}-${path.replace(/\./g, '-')}`); the click handler smooth-
// scrolls + focuses the target input.
//
// **Touched-gating mirrors the per-field display.** `<Form>` only
// computes errors when at least one field has been touched (D-087);
// the summary therefore stays empty on first render and lights up
// after any field interaction. Renders nothing while the errors map
// is empty so the surface stays out of the way during a clean form.
//
// **Path → label resolution.** Walks the schema-derived `FieldNode`
// tree (P-025 walker) along the dotted path, descending into array
// elements on numeric segments, into the last variant of a union
// (D-033), and into object children by name. The deepest matching
// node's `label` is the display label; falls back to a humanized
// final segment when the path doesn't fully resolve (e.g., the array
// itself failed `min(1)` and the path ends at the array name).
//
// **Smooth scroll + focus.** `scrollIntoView({behavior: 'smooth'})`
// + `el.focus({preventScroll: true})` lands the field in view AND
// gives keyboard users an actionable focus position. Falls back to a
// regular hash navigation when the target id isn't in the DOM (rare —
// would require a dynamic remount mid-click).

import { useMemo } from 'preact/hooks'
import type { JSX } from 'preact'

import type { FieldNode } from './schema-to-fields'
import { humanize } from './humanize'

interface ValidationSummaryProps {
  readonly errors: Readonly<Record<string, string>>
  readonly root: FieldNode
  readonly idPrefix: string
}

interface ValidationItem {
  readonly path: string
  readonly id: string
  readonly label: string
  readonly message: string
}

export function ValidationSummary({ errors, root, idPrefix }: ValidationSummaryProps) {
  const items = useMemo<readonly ValidationItem[]>(
    () =>
      Object.entries(errors).map(([path, message]) => ({
        path,
        id: pathToId(idPrefix, path),
        label: findLabelByPath(root, path) ?? humanize(lastSegment(path)),
        message,
      })),
    [errors, root, idPrefix]
  )

  if (items.length === 0) return null

  return (
    <div
      aria-labelledby={`${idPrefix}-validation-summary-title`}
      aria-live="polite"
      class="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
      data-testid="validation-summary"
    >
      <h3
        id={`${idPrefix}-validation-summary-title`}
        class="m-0 text-sm font-semibold"
      >
        Please fix {items.length} {items.length === 1 ? 'error' : 'errors'}:
      </h3>
      <ul class="m-0 mt-1 list-disc space-y-0.5 pl-5">
        {items.map((item) => (
          <li key={item.path}>
            <a
              href={`#${item.id}`}
              class="underline decoration-red-400 hover:decoration-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              onClick={(e) => handleClick(e, item.id)}
            >
              {item.label}: {item.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

function handleClick(event: JSX.TargetedMouseEvent<HTMLAnchorElement>, id: string): void {
  const el = document.getElementById(id)
  if (el === null) return // fallback: browser handles the hash navigation
  event.preventDefault()
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (typeof el.focus === 'function') el.focus({ preventScroll: true })
}

function pathToId(idPrefix: string, path: string): string {
  return `${idPrefix}-${path.replace(/\./g, '-')}`
}

function lastSegment(path: string): string {
  const parts = path.split('.')
  /* v8 ignore next -- split() always yields ≥1 element so the ?? branch is unreachable; kept for noUncheckedIndexedAccess type safety */
  return parts[parts.length - 1] ?? path
}

/**
 * Walk the FieldNode tree along a dotted path, returning the label of
 * the deepest matching node. Returns null when the path doesn't
 * resolve (e.g., a union with a non-object last variant, an array
 * leaf with a child-name segment, or a missing-named child).
 */
export function findLabelByPath(root: FieldNode, path: string): string | null {
  const parts = path.split('.')
  let node: FieldNode = root
  for (const part of parts) {
    if (node.kind === 'object') {
      const child = node.children.find((c) => c.name === part)
      if (child === undefined) return null
      node = child
      continue
    }
    if (node.kind === 'array') {
      // Numeric index → descend into the array's element schema.
      // Non-numeric segments after an array indicate the path was
      // built from a value that doesn't match the schema (rare); bail.
      if (!/^\d+$/.test(part)) return null
      node = node.element
      continue
    }
    if (node.kind === 'union') {
      const last = node.variants[node.variants.length - 1]
      if (last === undefined || last.kind !== 'object') return null
      const child = last.children.find((c) => c.name === part)
      if (child === undefined) return null
      node = child
      continue
    }
    return null
  }
  return node.label
}
